// ChatWelcomeModal — first-visit welcome for /chat (consumer AI advisor).
//
// Why a modal here vs. the inline banner on /workspace:
//   - /chat is the entry point for most consumer users. A blocking
//     modal makes the "you're now talking to an AI advisor, here's
//     what it can do" pitch unmissable, whereas an inline banner
//     would compete with the input area for attention.
//   - One-shot: localStorage flag, dismissed === seen, no re-show.
//   - Standalone (no portal lib): we mount it via createPortal to
//     document.body so it isn't trapped by ChatUI's flex hierarchy.
//
// SSR: bails to `dismissed=true` when window is missing.
// localStorage disabled (private mode): falls back to in-memory state
// for the session — user sees the modal once per page load, which is
// arguably correct behavior in private browsing anyway.

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { track } from "@/lib/analytics";
import { STORAGE_ONBOARDING } from "@/constants/storage";

const LS_KEY = STORAGE_ONBOARDING.chatWelcomeDismissed;

export function ChatWelcomeModal() {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    try {
      return window.localStorage.getItem(LS_KEY) === "1";
    } catch {
      return true;
    }
  });
  // Defer first paint slightly so the chat shell doesn't flicker behind
  // the modal on cold load. Also lets ChatUI mount before we layer on top.
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (dismissed) return;
    const id = window.setTimeout(() => setArmed(true), 200);
    return () => window.clearTimeout(id);
  }, [dismissed]);

  const close = () => {
    setDismissed(true);
    track("chat_welcome_dismissed");
    try { window.localStorage.setItem(LS_KEY, "1"); } catch { /* private mode */ }
  };

  // B2: seed the chat composer with one of the curated sample questions
  // and close the modal. ChatUI listens for `chat:seed-input` and only
  // fills — never auto-sends — so the user can tweak before submitting.
  // Decoupled via window CustomEvent to avoid prop-drilling state through
  // ChatUI's mode/conversationId/useChat boundary.
  const seedAndClose = (text: string) => {
    track("chat_sample_clicked", { length: text.length });
    try {
      window.dispatchEvent(new CustomEvent("chat:seed-input", { detail: { text } }));
    } catch {
      /* IE11/SSR safety; not expected in a Vite app but cheap */
    }
    close();
  };

  // Esc closes the modal — ARIA APG expectation for aria-modal dialogs.
  // Backdrop click already closes; this brings keyboard users to parity.
  useEffect(() => {
    if (dismissed || !armed) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dismissed, armed]);

  if (dismissed || !armed) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("chat.welcome.title")}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div
        style={{
          background: "var(--surface, #fff)",
          color: "var(--ink-1, #111)",
          borderRadius: 14,
          padding: "22px 24px",
          maxWidth: 480, width: "100%",
          boxShadow: "0 24px 64px rgba(0,0,0,0.35)",
          fontFamily: "var(--font-ui, system-ui, sans-serif)",
          border: "1px solid var(--rule, rgba(0,0,0,0.08))",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
          <div
            style={{
              width: 36, height: 36, borderRadius: 9,
              background: "var(--accent-soft, rgba(16,185,129,0.12))",
              color: "var(--accent, #10b981)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 18, flexShrink: 0,
            }}
            aria-hidden
          >👋</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.3, marginBottom: 4 }}>
              {t("chat.welcome.title")}
            </div>
            <div style={{ fontSize: 13.5, color: "var(--ink-2, #444)", lineHeight: 1.55 }}>
              {t("chat.welcome.body")}
            </div>
          </div>
        </div>
        <ul
          style={{
            listStyle: "none", padding: 0, margin: "8px 0 12px",
            display: "flex", flexDirection: "column", gap: 6,
          }}
        >
          {(["bullet1", "bullet2", "bullet3"] as const).map((k) => (
            <li
              key={k}
              style={{
                fontSize: 13, color: "var(--ink-2, #444)",
                paddingLeft: 18, position: "relative", lineHeight: 1.5,
              }}
            >
              <span
                aria-hidden
                style={{
                  position: "absolute", left: 0, top: 7,
                  width: 6, height: 6, borderRadius: 3,
                  background: "var(--accent, #10b981)",
                }}
              />
              {t(`chat.welcome.${k}`)}
            </li>
          ))}
        </ul>
        {/* B2: sample-question chips — clicking seeds the composer and
            closes the modal. Lowers the activation cost for first-time
            users who don't yet know what to ask. */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: "var(--ink-3, #666)", marginBottom: 6, letterSpacing: "0.02em" }}>
            {t("chat.welcome.tryLabel")}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {(["absd", "hdb", "comps"] as const).map((k) => {
              const text = t(`chat.welcome.samples.${k}`);
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => seedAndClose(text)}
                  style={{
                    textAlign: "left",
                    padding: "8px 10px",
                    borderRadius: 8,
                    border: "1px solid var(--rule, rgba(0,0,0,0.08))",
                    background: "var(--surface-2, rgba(0,0,0,0.02))",
                    color: "var(--ink-1, #111)",
                    cursor: "pointer",
                    fontSize: 12.5, lineHeight: 1.4,
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = "var(--accent-soft, rgba(16,185,129,0.08))";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = "var(--surface-2, rgba(0,0,0,0.02))";
                  }}
                >
                  {text}
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            onClick={close}
            style={{
              padding: "8px 16px", borderRadius: 7, border: "none",
              background: "var(--accent, #10b981)", color: "#fff",
              fontSize: 13, fontWeight: 600, cursor: "pointer",
            }}
          >
            {t("chat.welcome.cta")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
